import api from '@/api';
import Vue from 'vue';
import * as ArcGIS from '@/helpers/ArcGIS';
import {
  addToParent,
  findLayerById,
  hideAllLayersInTree,
  removeFromTree,
  updateInTree,
  updateVisibilityInTree,
} from '@/helpers/layer';
import {
  createSyncRequest,
  createSyncState,
  defineSyncSchema,
  mergeSyncResponse,
  recordWrite,
  schemaKinds,
  syncMutations,
} from '@/helpers/storeSync';

const LAYER_SYNC_SCHEMA = defineSyncSchema({
  key: 'id',
  kind: 'layer',
  insertAt: 'end',
  recursiveChild: 'children',
});

const state = {
  layers: [],
  etiquetasVisibles: false,
  sync: createSyncState(schemaKinds(LAYER_SYNC_SCHEMA)),
};

const getters = {
  layers: (state) => {
    return state.layers;
  },
  layersPanel: (state) => {
    return state.layers.filter((layer) => layer.showPanel);
  },
  layerById: (state) => (id) => {
    return findLayerById(state.layers, id);
  },
  etiquetasVisibles: (state) => state.etiquetasVisibles,
};

const mutations = {
  ...syncMutations,
  MERGE_LAYERS(state, { rows, request }) {
    state.layers = mergeSyncResponse(state.sync, state.layers, rows, LAYER_SYNC_SCHEMA, request);
  },
  SET_ETIQUETAS_VISIBLES(state, data) {
    state.etiquetasVisibles = data;
    ArcGIS.setLabelsLayer('layer-recursos', data);
    ArcGIS.setLabelsLayer('layer-infraestructuras-propias', data);
  },
  SET_VISIBILITY_LAYER(state, data) {
    updateVisibilityInTree(state.layers, {
      id: data.id,
      visible: data.visible,
    });
  },
  SET_OPACITY_LAYER(state, data) {
    const position = state.layers.findIndex((x) => x.id === data.id);

    if (position !== -1) {
      const layer = state.layers[position];
      layer.opacity = data.opacity;
      Vue.set(state.layers, position, layer);
    }
  },
  SET_DEFINITION_EXPRESION_LAYER(state, data) {
    const layer = findLayerById(state.layers, data.id)?.layer;

    if (layer) {
      let currentExpr = layer.definitionExpression || '';
      const opcionesActivo = [' OR ACTIVO = 1 ', ' OR ACTIVO = 0 '];

      if (opcionesActivo.includes(data.definitionExpression)) {
        const term = data.definitionExpression.trim();

        if (currentExpr.includes(term)) {
          const regex = new RegExp(term, 'g');
          layer.definitionExpression = currentExpr.replace(regex, '').replace(/\s\s+/g, ' ');
        } else {
          const antes = '(1=0';
          const despues = currentExpr.substring(antes.length);
          layer.definitionExpression = antes + data.definitionExpression + despues;
        }
      } else if (data.definitionExpression.includes('ID_RECURSO NOT IN')) {
        const term = data.definitionExpression.trim();
        layer.definitionExpression = currentExpr.includes(term)
          ? currentExpr.replace(term, '').trim()
          : currentExpr + data.definitionExpression;
      } else {
        const indexAnd = layer.definitionExpression.indexOf(') AND');

        if (indexAnd !== -1) {
          const antes = layer.definitionExpression.substring(0, indexAnd + 1);
          layer.definitionExpression = antes + data.definitionExpression;
        } else {
          layer.definitionExpression = layer.definitionExpression + data.definitionExpression;
        }
      }

      if (data.reset) {
        layer.definitionExpression = data.definitionExpression;
      }

      layer.definitionExpression = layer.definitionExpression.replace(/\s\s+/g, ' ').trim();
      ArcGIS.setDefinitionExpressionLayer(data.id, layer.definitionExpression);
      Vue.set(layer, 'definitionExpression', layer.definitionExpression);
    }
  },
  SOCKET_addLayer(state, newLayer) {
    const parentId = newLayer.parentId || null;

    if (!parentId) {
      state.layers.push(newLayer);
      recordWrite(state.sync, 'layer', newLayer.id);
    } else {
      const added = addToParent(state.layers, newLayer, parentId);
      if (added) {
        recordWrite(state.sync, 'layer', newLayer.id);
        ArcGIS.createLayers([newLayer]);
        this.notify('Capa añadida', `Se ha añadido la capa ${newLayer.title}`, 'success');
      } else {
        this.notify('Error', `La capa ${newLayer.title} no se ha podido añadir`, 'error');
      }
    }
  },
  SOCKET_deleteLayer(state, { layerId, code }) {
    const deleted = removeFromTree(state.layers, layerId);
    recordWrite(state.sync, 'layer', layerId, true);

    if (deleted) {
      ArcGIS.deleteLayerById(code);
      this.notify('Capa eliminada', `Se ha eliminado la capa`);
    }
  },
  SOCKET_deleteGroup(state, { groupId }) {
    const deleted = removeFromTree(state.layers, groupId);
    recordWrite(state.sync, 'layer', groupId, true);

    if (deleted) {
      this.notify('Carpeta eliminada', 'Se ha eliminado la carpeta');
    }
  },
  SOCKET_renameLayer(state, layer) {
    const updated = updateInTree(state.layers, layer);

    if (updated) {
      recordWrite(state.sync, 'layer', layer.id);
      this.notify('Capa renombrada', `Nuevo nombre: ${layer.title}`, 'success');
    }
  },
};

const actions = {
  async fetchLayers({ commit, state }) {
    try {
      commit('BEGIN_SYNC_REQUEST');
      const request = createSyncRequest(state.sync.seq);
      const response = await api.layers.getLayers();
      commit('MERGE_LAYERS', { rows: response.data, request });
    } catch (error) {
      console.error(`Error fetchLayers | ${error}`);
      throw error;
    }
  },
  setVisibilityLayer({ dispatch, commit, _state }, data) {
    if (data.id !== 'layer-recursos') {
      commit('SET_VISIBILITY_LAYER', data);
    }

    const visibleSend = {
      visible: data.visible,
      id: data.id,
    };
    dispatch('shareMap/updateLayerVisibility', visibleSend, { root: true });

    switch (data.id) {
      case 'layer-recursos':
        commit('SET_DEFINITION_EXPRESION_LAYER', {
          id: 'layer-recursos',
          definitionExpression: ' OR ACTIVO = 1 ',
          add: data.visible,
        });
        break;

      case 'layer-recursos-inactivos':
        commit('SET_DEFINITION_EXPRESION_LAYER', {
          id: 'layer-recursos',
          definitionExpression: ' OR ACTIVO = 0 ',
          add: data.visible,
        });
        break;

      case 'layer-etiquetas':
        commit('SET_ETIQUETAS_VISIBLES', data.visible);
        break;
    }

    ArcGIS.setVisibilityLayer(data.id, data.visible);
  },
  setOpacityLayer({ dispatch, commit, _state }, data) {
    commit('SET_OPACITY_LAYER', data);

    const opacitySend = {
      opacity: data.opacity,
      id: data.id,
    };
    dispatch('shareMap/updateLayerOpacity', opacitySend, { root: true });
  },
  setDefinitionExpression({ commit }, data) {
    commit('SET_DEFINITION_EXPRESION_LAYER', data);
  },
  async uploadLayer({ _commit }, layerData) {
    try {
      await api.layers.updaloadLayer(layerData);
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
  async importService({ _commit }, layerData) {
    try {
      await api.layers.importService(layerData);
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
  async deleteLayerItem({ _commit }, { layerId, code }) {
    try {
      await api.layers.deleteLayerItem(layerId, code);
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
  async deleteGroupItem({ _commit }, groupId) {
    try {
      await api.layers.deleteGroupItem(groupId);
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
  async createLayerGroup({ _commit }, layerGroupData) {
    try {
      await api.layers.createLayerGroup(layerGroupData);
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
  async renameItem({ _commit }, data) {
    try {
      await api.layers.renameItem(data);
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
  hideAllLayers({ state, dispatch }) {
    hideAllLayersInTree(
      state.layers.filter((layer) => layer.showPanel),
      dispatch
    );
  },
  async downloadLayerItem({ _commit }, data) {
    try {
      const response = await api.layers.downloadLayerItem(data);
      return response;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
};

const module = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};

export default module;
